import React, { useEffect, useMemo, useState } from 'react';

// External Dependencies
import { TextareaAutosize, TextField } from '@material-ui/core';
import PropTypes from 'prop-types';
import moment from 'moment';

// Internal Dependencies
import MText from '../../../../../components/Common/MText';
import Colours from '../../../../../styles/colours';
import RadioBtnSelect from '../../../../../components/Common/RadioBtnSelect';
import MaterialDatePicker from '../../../../../components/Common/form/MaterialDatePicker';
import { StandardLCTnC, Status } from '../../../../../common/constants';
import { IsEmpty } from '../../../../../common/checks';
import LoyaltyMenuDescriptionBox from '../../../../../components/LoyaltyMenuDescriptionBox';
import withLoyaltyConnect from '../WithLoyaltyConnect';

const ValidityTerms = (props) => {
  const [isValidityByDateRange, setIsValidityByDateRange] = useState(IsEmpty(props.loyaltyProgram.validityInMonths));

  const isDraft = props.loyaltyProgram.status === Status.Draft;
  const isDeactivated = props.loyaltyProgram.status === Status.Deactivated;

  const { stamp, vouchers, validityInMonths } = props.loyaltyProgram;

  const findFirstValidVoucher = (vouchersList) => {
    for (let i = 0; i < vouchersList.length; i += 1) {
      const vc = vouchersList[i];
      const isLast = i === vouchersList.length - 1;

      if (vc.issuableQty == null || vc.balQty > 0 || isLast) {
        return vc;
      }
    }

    return null; // if no voucher matched, which technically shouldn't happen due to last voucher fallback
  };

  const selectedVoucher = IsEmpty(vouchers)
    ? null
    : findFirstValidVoucher(vouchers);

  const updatedStandardLCTnC = StandardLCTnC
    .replace('X', !IsEmpty(stamp.amountPerStamp) ? stamp.amountPerStamp : 'X')
    .replace('Y', !IsEmpty(stamp.numOfStamps) ? stamp.numOfStamps : 'Y')
    .replace('$Z', !IsEmpty(selectedVoucher?.terms) ? selectedVoucher.terms : '$Z');

  const isError = useMemo(() => !isValidityByDateRange && !IsEmpty(validityInMonths) && validityInMonths <= 0, [isValidityByDateRange, props.loyaltyProgram.validityInMonths]);

  const formatDate = (date) => {
    const newDate = new Date(date);
    return moment(newDate).format('YYYY-MM-DD');
  };

  useEffect(() => {
    if (isError) {
      props.disableSaveBtnAction();
    } else {
      props.enableSaveBtnAction();
    }
  }, [isError]);

  useEffect(() => () => {
    props.enableSaveBtnAction();
  }, []);

  return (
    <div className="container-fluid px-0 pt-3">
      <LoyaltyMenuDescriptionBox
        text="View the pre-set standard loyalty card T&Cs and add on any additional T&Cs as necessary.  Set the validity period of the loyalty card with a fixed end date, or with a fixed period (X months after the 1st stamp is collected)."
      />
      <div className="mt-4">
        <MText bold text="Loyalty Card Validity" size={14} colour={Colours.Gray} />
        <div className="mt-2 ml-2">
          <RadioBtnSelect
            options={[{
              label:    'Fixed Expiry Date',
              subLabel: 'Select Expiry Date',
              value:    true,
            }]}
            value={isValidityByDateRange}
            onChange={() => {
              setIsValidityByDateRange(true);
              props.updateLoyaltyCardAction({ validityInMonths: '' });
            }}
            labelStyle={{ fontWeight: 700 }}
            radioWrapperStyle={{ alignItems: 'flex-start' }}
            radioStyle={{ margin: '0px 9px 9px 9px', padding: 0 }}
            disabled={!isDraft}
          />
          <MaterialDatePicker
            minDate={false}
            variant="outlined"
            value={props.loyaltyProgram.expiryDate}
            disablePast
            disabled={!isValidityByDateRange || !isDraft}
            onChange={(e) => props.updateLoyaltyCardAction({ expiryDate: e ? formatDate(e.toString('yyyy-MM-dd')) : null })}
            className="mt-0"
            style={{ marginLeft: 32 }}
          />
        </div>

        <div className="mt-2 ml-2">
          <RadioBtnSelect
            options={[{
              label:    'Fixed Expiry Period',
              subLabel: 'Fill in the number of months the card is valid for, after the first stamp is issued',
              value:    true,
            }]}
            value={!isValidityByDateRange}
            onChange={() => {
              setIsValidityByDateRange(false);
              props.updateLoyaltyCardAction({ expiryDate: null });
            }}
            disabled={!isDraft}
            labelStyle={{ fontWeight: 700 }}
            radioWrapperStyle={{ alignItems: 'flex-start' }}
            radioStyle={{ margin: '0px 9px 9px 9px', padding: 0 }}
          />
          <div className="d-flex flex-row align-items-center">
            <TextField
              name="validityInMonths"
              variant="outlined"
              size="small"
              type="number"
              className="mr-2"
              disabled={isValidityByDateRange || !isDraft}
              value={props.loyaltyProgram.validityInMonths}
              onChange={(e) => props.updateLoyaltyCardAction({ validityInMonths: e.target.value ? parseInt(e.target.value, 10) : '' })}
              style={{ marginLeft: 32, width: 237 }}
              inputProps={{ min: 1 }}
              error={isError}
              helperText={isError ? 'Minimum 1 month required' : undefined}
              FormHelperTextProps={{
                className: 'position-absolute',
                style:     {
                  bottom:     -24,
                  marginLeft: 0,
                },
              }}
            />
            <MText text="Month(s)" size={12} colour={Colours.Gray} />
          </div>
        </div>
      </div>
      <div className="mt-4">
        <MText bold text="Loyalty Card Terms & Conditions" size={14} colour={Colours.Gray} />
        <div className="mt-2" style={{ marginLeft: 40 }}>
          <MText text="Standard T&Cs" size={12} />
          <MText
            className="ml-2"
            text={updatedStandardLCTnC}
            style={{ lineHeight: 1.2 }}
          />

          <div className="mt-2">
            <MText text="Additional T&Cs (Please add as necessary)" />
            <TextareaAutosize
              className="w-100 p-2"
              minRows={5}
              maxRows={5}
              disabled={isDeactivated}
              value={props.loyaltyProgram.additionalTnC}
              onChange={(e) => props.updateLoyaltyCardAction({ additionalTnC: e.target.value })}
              style={{
                resize:       'none',
                background:   'transparent',
                border:       `1px solid ${Colours.Gray5}`,
                borderRadius: 10,
                fontSize:     12,
                lineHeight:   1.2,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

ValidityTerms.propTypes = {
  loyaltyProgram: PropTypes.shape({
    expiryDate:       PropTypes.string,
    validityInMonths: PropTypes.number,
    additionalTnC:    PropTypes.string,
    status:           PropTypes.oneOf([Status.Draft, Status.Live, Status.Deactivated]),
    stamp:            PropTypes.shape({
      numOfStamps:    PropTypes.number,
      amountPerStamp: PropTypes.number,
    }),
    vouchers: PropTypes.arrayOf(PropTypes.shape({
      terms: PropTypes.string,
    })),
  }).isRequired,
  updateLoyaltyCardAction: PropTypes.func.isRequired,
  disableSaveBtnAction:    PropTypes.func.isRequired,
  enableSaveBtnAction:     PropTypes.func.isRequired,
};

export default withLoyaltyConnect(ValidityTerms);
